import React from 'react';
import { AffiliationFilterForm } from './AffiliatedFilter';
import dotProp from 'dot-prop-immutable';
import axiosInstance from 'utils/axiosUtil';
import { CsvUtil, DataUtil, SpecialtyUtil, CsvExportUtil, QueryUtil } from 'doctivity-shared/utils';
import { AffiliatedIcon } from 'components';

/**
 * Format a cell value
 * @name DataMapper
 * @function
 * @param {unknown} value
 * @param {unknown} row
 * @param {string} columnName
 * @param {ColumnMapping} mapping
 */

/**
 * @typedef {Object} ColumnMapping
 * @property {string} displayName - The user-facing name (if different from the key for this entry)
 * @property {string?} helpText - Extra help text (often includes column names that will be in the download)
 * @property {string?} downloadName - The CSV column name (if different than displayName)
 * @property {string?} type - Data type (string, number, boolean)
 * @property {boolean?} displayable - Whether to include the column in the user facing table (default: true)
 * @property {boolean?} downloadable - Whether to include this column in the download (default: true)
 * @property {DataMapper?} format - Optional method to produce the output value for this column
 */
/**
 * @typedef {Function} QueryStoreApplier
 * @param {Object} store - The current redux store
 * @param {Object} query - The query to be sent to the server
 * @returns {Object} The modified query
 */
/**
 * @typedef {Object} DataDownload
 * @property {string} displayName - The user facing name of the data
 * @property {string} downloadName - The name of the download
 *   This can use the following substitutions: {date}: dd-MM-yyyy will be inserted
 * @property {string} endpoint - The endpoint which the initialQuery is valid against
 * @property {Array} initialQuery - The initial query structure
 * @property {Object?} filterForm - An optional extra form for specific filtering
 * @property {Object.<string, ColumnMapping>} columnMapping - The mapping from query column names to user-facing columns names
 * @property {QueryStoreApplier?} applyStore - An optional method which can extract values from the current store and apply them to the query, executed before the query is sent to the server
 **/

/**
 * Scope the Affiliated-icon `clients` include to a single client, on the PIVOT (through.where
 * client_id) so the join stays LEFT (required:false) unless a filter already made it required —
 * every provider is still returned, the icon just reflects THIS client, and no other client's
 * name is exposed. No-op when the query has no `clients` include (the Contacts reports).
 * @param {Object} query - the query being prepared (mutated in place)
 * @param {number} selectedClient
 */
function scopeAffiliatedIcon(query, selectedClient) {
  if (!query || !Array.isArray(query.include)) return;
  const clientsInc = query.include.find((i) => i.association === 'clients');
  if (!clientsInc) return;
  clientsInc.through = {
    ...(clientsInc.through || {}),
    attributes: [],
    where: {
      ...(clientsInc.through && clientsInc.through.where),
      client_id: selectedClient,
      // is_affiliated: true so the icon means "affiliated to THIS client" — the same definition
      // the Affiliated filter uses. Without it the icon also lit for providers who merely have a
      // ProviderClients row for the client (e.g. a client-provided email with is_affiliated=false),
      // so "All" showed more icons than the "Affiliated" filter returned rows.
      is_affiliated: true,
    },
  };
  if (clientsInc.required === undefined) {
    clientsInc.required = false;
  }
}

/** @type {DataDownload[]} */
/**
 * Format a US postal code for display and CSV export.
 *
 * Postal codes reach us in inconsistent shapes — ZIP+4, stray punctuation from imports, empty
 * strings, and numeric values that lost a leading zero (00501 -> 501) — and the column is also
 * rendered for rows whose joined location is null, where the value is `undefined`.
 *
 * The previous `\`000000${String(v)}\`.slice(-5)` mangled every one of those: undefined became
 * the literal string "fined", null became "0null", "12345-6789" became "-6789", and a trailing
 * comma shifted the digits. Return '' when there is no usable value rather than inventing one —
 * a blank cell in a mailing-address export is honest; a fabricated zip is not.
 */
function formatZip(v) {
  if (v === null || v === undefined) return '';
  // Keep the 5-digit base of a ZIP+4, then drop any non-digit noise.
  const digits = String(v).trim().split('-')[0].replace(/\D/g, '');
  if (!digits) return '';
  return digits.length < 5 ? digits.padStart(5, '0') : digits.slice(0, 5);
}

const availableDownloads = [
  {
    displayName: 'Provider Mailing Addresses',
    helpText:
      'Download a .csv of Provider Mailing Addresses. Fields: NPI, First Name, Last Name, Credential, Location Name, Specialty, Address 1, Address 2, City, State, Zip, Email',
    endpoint: 'Providers',
    downloadName: 'doctivity-providers-address_{date}.csv',
    filterForm: AffiliationFilterForm,
    applyStore: (store, query) => {
      const {
        app: { selectedClient },
      } = store;
      const newQuery = DataUtil.deepCopy(query);
      // Scope the Affiliated-icon `clients` include to the selected client. Under "All" (and on
      // first load) the include arrives unscoped, which would light the icon for affiliation to
      // ANY client — and return other clients' names in the JSON. Scoping on the PIVOT
      // (through.where client_id) with required:false keeps it a LEFT join, so every provider is
      // still listed; the icon just reflects THIS client. (Affiliated mode already scopes it.)
      scopeAffiliatedIcon(newQuery, selectedClient);
      // Scope the email on the PIVOT (through.where), not on the target (where: { id }).
      //
      // Two reasons. Semantically the thing being scoped is the ProviderClients row that
      // carries the email, not the Clients row. Practically, crud.findAll picks its count
      // strategy with `!include.find(i => i.required || hasNestedWhere(i))` (src/crud.js), and
      // hasNestedWhere inspects `where` / `include` but NOT `through` — so a `where` here flips
      // the whole request onto findAndCountAll, which counts over the joins without `distinct`
      // and so multiplies any provider affiliated to more than one client.
      //
      // Measured on dev (1,071,142 providers): where-on-target gave 1,071,149 in 8,628ms;
      // through.where gives the true 1,071,142 in ~200ms, with identical email values.
      // That count is displayed to the user AND drives the export dialog's start-row batches,
      // so an inflated one offers a batch that runs past the end of the data.
      newQuery.include.push({
        association: 'related_clients',
        attributes: ['id'],
        through: {
          as: 'options',
          attributes: ['is_affiliated', 'rvu_goal', 'email'],
          where: {
            client_id: selectedClient,
          },
        },
        required: false,
      });
      return newQuery;
    },
    initialQuery: {
      where: {},
      include: [
        {
          association: 'clients',
          attributes: ['id', 'name'],
        },
        {
          association: 'taxonomy',
        },
        {
          association: 'location',
        },
      ],
      order: [['id', 'ASC']],
    },
    columnMapping: {
      clients: {
        displayName: '',
        downloadName: 'Affiliated',
        sortable: false,
        filter: false,
        style: { width: 50 },
        format: (v, _row, _key, mapping) => {
          if (Array.isArray(v) && v.length > 0) {
            return typeof mapping === 'object' &&
              mapping.downloadName !== undefined ? (
              'affiliated'
            ) : (
              <AffiliatedIcon />
            );
          }
        },
      },
      npi: {
        displayName: 'NPI',
        displayable: false,
      },
      last_name: {
        displayName: 'Last Name',
      },
      first_name: {
        displayName: 'First Name',
      },
     'taxonomy.classification': {
        displayName: 'Specialty',
        downloadable: false,
        format: (_, row) => row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '',
        filterFields: ['primary_taxonomy'],
        specialtySearch: true,
      },      
      'taxonomy.classification2': {
        displayName: 'Specialty',
        displayable: false,
        downloadable: true,
        format: (_, row) => row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '',
      },
      'taxonomy.classification3': {
        displayName: 'Code',
        displayable: false,
        downloadable: true,
        format: (_, row) => row.taxonomy?.code || '',
      },
      credential: {
        displayName: 'Credential',
        displayable: false,
      },
      'location.name': {
        displayName: 'Location Name',
        displayable: false,
      },
      'location.address1': {
        displayName: 'Address',
        downloadName: 'Address 1',
      },
      'location.address2': {
        displayName: 'Address 2',
        displayable: false,
      },
      'location.city': {
        displayName: 'City',
      },
      'location.state': {
        displayName: 'State',
        format: (v) => v?.toUpperCase(),
        style: { width: 50 },
      },
      'location.postal_code': {
        displayName: 'Zip',
        format: formatZip,
        style: { width: 80 },
      },
      'related_clients': {
        // Client-provided email lives in the ProviderClients pivot (related_clients.options.email),
        // falling back to the provider's own email. Now shown in the on-screen table as well as the
        // CSV. sortable/filter are off: the value is a through-table column the generic /Providers
        // endpoint can't order or filter on server-side.
        displayName: 'Email',
        sortable: false,
        filter: false,
        style: { minWidth: 160 },
        format: (v, row) => {
          if (Array.isArray(v) && v.length > 0) {
            return v[0].options?.email || row.email;
          }
          return row.email;
        }
      }
    },
  },
  {
    displayName: 'Provider Emails',
    helpText:
      'Download a .csv of Providers who have an email on file (client-provided, or the provider’s own). Fields: NPI, First Name, Last Name, Credential, Specialty, Email',
    endpoint: 'Providers',
    downloadName: 'doctivity-providers-emails_{date}.csv',
    filterForm: AffiliationFilterForm,
    // "Has an email" must match the DISPLAYED value: COALESCE(client pivot email, provider.email).
    // The pivot email lives on ProviderClients.email and the fallback on Providers.email, so it
    // can't be a plain column filter or an INNER join on the pivot (that would drop providers whose
    // only email is on Providers.email — e.g. an unaffiliated provider with a provider email). The
    // $emailProvidedForClient key is expanded by crud.js sanitizeWhere into a NON-correlated id
    // set — pivot ids UNION provider ids — materialized once and probed by primary key, keeping a
    // provider when EITHER email is non-empty. (It was a correlated subquery, one ProviderClients
    // lookup per provider, which full-scanned Providers at ~2.5s per keystroke; see the literal in
    // crud.js for why the `SELECT id FROM (...) email_ids` wrapper is required rather than a bare
    // IN (... UNION ...).) related_clients stays a LEFT join purely to supply the displayed value.
    applyStore: (store, query) => {
      const {
        app: { selectedClient },
      } = store;
      const newQuery = DataUtil.deepCopy(query);
      // Scope the Affiliated-icon `clients` include to the selected client (see report 1).
      scopeAffiliatedIcon(newQuery, selectedClient);
      newQuery.where = {
        ...(newQuery.where || {}),
        $emailProvidedForClient: selectedClient,
      };
      newQuery.include.push({
        association: 'related_clients',
        attributes: ['id'],
        through: {
          as: 'options',
          attributes: ['is_affiliated', 'rvu_goal', 'email'],
          where: {
            client_id: selectedClient,
          },
        },
        required: false,
      });
      return newQuery;
    },
    initialQuery: {
      where: {},
      // distinct so the count stays exact when the Affiliated filter promotes the multi-row
      // `clients` belongsToMany include to a required (INNER) join and flips the request onto
      // findAndCountAll — otherwise a provider affiliated to more than one client is counted twice.
      // (Under "All" the count runs on the fast count() path, where distinct is a harmless no-op.)
      distinct: true,
      include: [
        {
          association: 'clients',
          attributes: ['id', 'name'],
        },
        {
          association: 'taxonomy',
        },
      ],
      order: [['last_name', 'ASC']],
    },
    columnMapping: {
      clients: {
        displayName: '',
        downloadName: 'Affiliated',
        sortable: false,
        filter: false,
        style: { width: 50 },
        format: (v, _row, _key, mapping) => {
          if (Array.isArray(v) && v.length > 0) {
            return typeof mapping === 'object' &&
              mapping.downloadName !== undefined ? (
              'affiliated'
            ) : (
              <AffiliatedIcon />
            );
          }
        },
      },
      npi: {
        displayName: 'NPI',
        displayable: false,
      },
      last_name: {
        displayName: 'Last Name',
      },
      first_name: {
        displayName: 'First Name',
      },
      credential: {
        displayName: 'Credential',
      },
      'taxonomy.classification': {
        displayName: 'Specialty',
        downloadable: false,
        format: (_, row) =>
          row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '',
        filterFields: ['primary_taxonomy'],
        specialtySearch: true,
      },
      'taxonomy.classification2': {
        displayName: 'Specialty',
        displayable: false,
        downloadable: true,
        format: (_, row) =>
          row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '',
      },
      related_clients: {
        displayName: 'Email',
        sortable: false,
        filter: false,
        style: { minWidth: 160 },
        format: (v, row) => {
          if (Array.isArray(v) && v.length > 0) {
            return v[0].options?.email || row.email;
          }
          return row.email;
        },
      },
    },
  },
  {
    displayName: 'Contact Mailing Addresses',
    helpText:
      'Download a .csv of Contact Mailing Addresses. Fields: Last Name, First Name, Title, Organization, Company, Address 1, Address 2, City, State, Zip, Owner, Email, Phone',
    endpoint: 'Contacts',
    downloadName: 'doctivity-contacts-address_{date}.csv',
    // Scope to the selected client (Contacts is tenant-owned). Mirrors ContactsPage's
    // query.where.client_id and is applied to BOTH the on-screen table and the CSV.
    applyStore: (store, query) => {
      const {
        app: { selectedClient },
      } = store;
      const newQuery = DataUtil.deepCopy(query);
      newQuery.where = { ...(newQuery.where || {}), client_id: selectedClient };
      return newQuery;
    },
    initialQuery: {
      where: {},
      include: [
        // No explicit `required: false` here. Sequelize defaults an include that carries a
        // `where` to required: true (INNER), which is what makes a column filter actually drop
        // rows; setting required: false overrides that and silently turns every Organization /
        // Owner filter into a no-op. prepareQuery() promotes these when a filter is present and
        // leaves them LEFT-joined when it is not (see QueryUtil.promoteFilteredIncludes).
        {
          association: 'location',
          include: [
            {
              association: 'organization',
              attributes: ['id', 'name'],
            },
          ],
        },
        {
          association: 'owner',
          attributes: ['id', 'first_name', 'last_name'],
        },
      ],
      order: [['last_name', 'ASC']],
    },
    columnMapping: {
      last_name: {
        displayName: 'Last Name',
      },
      first_name: {
        displayName: 'First Name',
      },
      title: {
        // "Contact type" per the client's terminology maps to Title.
        displayName: 'Title',
      },
      'location.organization.name': {
        displayName: 'Organization',
        sortable: false,
        format: (_, row) => row.location?.organization?.name || '',
      },
      company: {
        displayName: 'Company',
      },
      'location.address1': {
        displayName: 'Address',
        downloadName: 'Address 1',
      },
      'location.address2': {
        displayName: 'Address 2',
        displayable: false,
      },
      'location.city': {
        displayName: 'City',
      },
      'location.state': {
        displayName: 'State',
        format: (v) => v?.toUpperCase(),
        style: { width: 50 },
      },
      'location.postal_code': {
        displayName: 'Zip',
        format: formatZip,
        style: { width: 80 },
      },
      'owner.last_name': {
        displayName: 'Owner',
        sortable: false,
        format: (_, row) =>
          row.owner ? `${row.owner.last_name}, ${row.owner.first_name}` : '',
      },
      email: {
        displayName: 'Email',
        style: { minWidth: 160 },
      },
      phone: {
        displayName: 'Phone',
        style: { minWidth: 100 },
      },
    },
  },
  {
    displayName: 'Contact Emails',
    helpText:
      'Download a .csv of Contacts who have an email on file. Fields: Last Name, First Name, Title, Organization, Company, Email',
    endpoint: 'Contacts',
    downloadName: 'doctivity-contacts-emails_{date}.csv',
    // Scope to the selected client AND only contacts with a non-empty email. In MySQL
    // `email <> ''` also excludes NULL (the predicate evaluates to NULL → row dropped), so a
    // single $ne condition covers both "empty" and "missing". email is a real Contacts column,
    // so this needs no backend change.
    applyStore: (store, query) => {
      const {
        app: { selectedClient },
      } = store;
      const newQuery = DataUtil.deepCopy(query);
      newQuery.where = {
        ...(newQuery.where || {}),
        client_id: selectedClient,
        email: { $ne: '' },
      };
      return newQuery;
    },
    initialQuery: {
      where: {},
      include: [
        {
          association: 'location',
          include: [
            {
              association: 'organization',
              attributes: ['id', 'name'],
            },
          ],
        },
      ],
      order: [['last_name', 'ASC']],
    },
    columnMapping: {
      last_name: {
        displayName: 'Last Name',
      },
      first_name: {
        displayName: 'First Name',
      },
      title: {
        displayName: 'Title',
      },
      'location.organization.name': {
        displayName: 'Organization',
        sortable: false,
        format: (_, row) => row.location?.organization?.name || '',
      },
      company: {
        displayName: 'Company',
      },
      email: {
        displayName: 'Email',
        style: { minWidth: 160 },
      },
    },
  },
];

/**
 * Returns the DynamicTable columns for the given download type
 * @param {string} index - The index of the entry in availableDownloads to extract column definitions from
 */
function getColumns(idx) {
  const entry = availableDownloads[idx];
  return Object.entries(entry.columnMapping)
    .map(([name, e]) => {
      if (e.displayable === false) return undefined;

      return {
        key: name,
        label: e.displayName ?? name,
        sortable: e.sortable,
        filter: e.filter,
        format: e.format,
        style: e.style,
        filterFields: e.filterFields,
        specialtySearch: e.specialtySearch,
        customFilterLabel: e.cystomFilterLabel,
        customFilterText: e.customFilterText
      };
    })
    .filter((v) => v !== undefined);
}

/**
 * @param {DataDownload} downloadInfo
 * @param {Object} query
 * @param {Object} store
 */
/**
 * @param {DataDownload} downloadInfo
 * @param {Object} query
 * @param {Object} store
 */
/**
 * Prepare a query for the server: apply any store-derived associations, then promote
 * filtered includes so column filters on joined columns (Organization, Owner, …) actually
 * drop rows instead of just nulling the joined data.
 *
 * Used by BOTH the on-screen table and the CSV export so the two cannot diverge — a filter
 * the user can see must be the same filter the download applies.
 *
 * Always returns a copy: applyStore deep-copies, and we deep-copy when it is absent, so
 * promoteFilteredIncludes (which mutates in place) never touches component state.
 */
function prepareQuery(downloadInfo, query, store) {
  const prepared = downloadInfo.applyStore
    ? downloadInfo.applyStore(store, query)
    : DataUtil.deepCopy(query);
  QueryUtil.promoteFilteredIncludes(prepared.include);
  return prepared;
}

async function getCSV(downloadInfo, query, store, options = {}) {
  let dataQuery = DataUtil.deepCopy(query);
  // Row count / start row come from the CSV export dialog. Clamp to the per-export cap
  // (CsvExportUtil, keyed by endpoint) — 5000 was overrunning the ~6 MB response wall in
  // prod. The backend has no cap on this JSON fetch, so the clamp here is the guard.
  const offset = parseInt(options.offset, 10);
  dataQuery.offset = Number.isFinite(offset) && offset > 0 ? offset : 0;
  dataQuery.limit = CsvExportUtil.clampLimit(options.limit, downloadInfo.endpoint);
  dataQuery = prepareQuery(downloadInfo, dataQuery, store);
  
  try {
    const results = await axiosInstance.get(`/${downloadInfo.endpoint}`, {
      params: {
        opts: dataQuery,
      },
      timeout: 60000, // 60 second timeout (increase from default)
    });
    
    if (!results || !Array.isArray(results.data?.rows)) {
      throw new Error('Unable to retrieve data for download. Please try again.');
    }
    
    return convertToCSV(downloadInfo, results.data.rows);
  } catch (error) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error('Download request timed out. Please try with fewer filters or contact support.');
    }
    throw error;
  }
}

/**
 * @param {DataDownload} downloadInfo
 * @param {Array} data
 */
function convertToCSV(downloadInfo, rows) {
  const columnMap = Object.entries(downloadInfo.columnMapping)
    .filter(
      ([, mapping]) =>
        mapping.downloadable || mapping.downloadable === undefined,
    )
    .reduce((acc, [key, mapping]) => {
      acc[key] = mapping;
      return acc;
    }, {});
  if (!rows || !Array.isArray(rows)) {
    throw new Error('Unable to use data for download. Please try again.');
  }
  const csvData = [];
  const csvIndexMap = {};
  rows.forEach((row, idx) => {
    if (idx === 0) {
      const header = [];
      Object.keys(columnMap).forEach((key) => {
        csvIndexMap[key] = columnMap[key];
        header.push(columnMap[key].downloadName ?? columnMap[key].displayName);
      });
      csvData.push(header);
    }
    const formattedRow = [];
    Object.entries(csvIndexMap).forEach(([key, mapping]) => {
      const value = dotProp.get(row, key);
      let formatted = mapping.format
        ? mapping.format(value, row, key, mapping)
        : value;
      if (typeof formatted === 'string') {
        formatted = `"${formatted.replace('"', '"')}"`;
      }
      formattedRow.push(formatted);
    });
    csvData.push(formattedRow);
  });
  const csv = CsvUtil.matrixToString(csvData);
  return csv;
}

export { getColumns, availableDownloads, getCSV, convertToCSV, prepareQuery };